/**
 * Internet speed test (download bandwidth + upload bandwidth + latency).
 *
 * Probes the bandwidth between THIS device and the ZA Player Pro backend
 * (zaplayerpro.club in production, the preview URL in dev). This is the
 * connection the player actually uses for licensing + admin traffic, and
 * it's a closer match to typical IPTV stream paths than a Cloudflare edge
 * the network might cache aggressively.
 *
 * Strategy:
 *   1. Latency  = avg round-trip of three small `GET /speedtest/down/1024`.
 *   2. Download = stream progressively larger payloads from
 *      `/api/speedtest/down/{bytes}` (1 MB → 10 MB → 25 MB).
 *   3. Upload   = POST random buffers (1 MB → 5 MB → 10 MB) to
 *      `/api/speedtest/up` and time the wall clock.
 */
import React, { useState } from "react";
import { Gauge, Loader2, Play, ArrowDown, ArrowUp } from "lucide-react";
import { API } from "../lib/playerApi";

const SPEEDTEST_HOST = API; // ends with /api — backend speedtest routes hang off it
const DOWN_SIZES = [
  { bytes: 1_000_000, label: "1 MB" },
  { bytes: 10_000_000, label: "10 MB" },
  { bytes: 25_000_000, label: "25 MB" },
];
const UP_SIZES = [
  { bytes: 1_000_000, label: "1 MB" },
  { bytes: 5_000_000, label: "5 MB" },
  { bytes: 10_000_000, label: "10 MB" },
];

async function fetchBytes(url) {
  const r = await fetch(url, { cache: "no-store" });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const buf = await r.arrayBuffer();
  return buf.byteLength;
}

// Generates a random payload of `size` bytes and POSTs it to the backend's
// /speedtest/up endpoint. Returns the bytes-per-second observed by the
// client (which is upload bandwidth + tiny request-overhead).
async function uploadBytes(size) {
  // crypto.getRandomValues is capped at 65 536 bytes per call → fill in chunks.
  const buf = new Uint8Array(size);
  const chunk = 65536;
  for (let i = 0; i < size; i += chunk) {
    crypto.getRandomValues(buf.subarray(i, Math.min(i + chunk, size)));
  }
  const t = performance.now();
  const r = await fetch(`${SPEEDTEST_HOST}/speedtest/up`, {
    method: "POST",
    body: buf,
    cache: "no-store",
    headers: { "Content-Type": "application/octet-stream" },
  });
  await r.arrayBuffer();
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const elapsed = (performance.now() - t) / 1000;
  return { bps: (size * 8) / elapsed, elapsed };
}

async function measureLatency() {
  const samples = [];
  for (let i = 0; i < 3; i++) {
    const t = performance.now();
    try {
      await fetchBytes(`${SPEEDTEST_HOST}/speedtest/down/1024?t=${Date.now()}`);
    } catch {
      return null;
    }
    samples.push(performance.now() - t);
  }
  return Math.round(samples.reduce((a, b) => a + b, 0) / samples.length);
}

function fmtMbps(bps) {
  const mbps = bps / 1_000_000;
  if (mbps >= 100) return mbps.toFixed(0);
  if (mbps >= 10) return mbps.toFixed(1);
  return mbps.toFixed(2);
}

function downloadRatingColor(mbps) {
  if (mbps >= 50) return "text-emerald-400";
  if (mbps >= 25) return "text-lime-400";
  if (mbps >= 10) return "text-amber-400";
  return "text-rose-400";
}

function downloadRatingText(mbps) {
  if (mbps >= 50) return "Great for 4K";
  if (mbps >= 25) return "Great for 1080p";
  if (mbps >= 10) return "OK for 720p";
  if (mbps >= 4) return "Marginal";
  return "Too slow";
}

// Upload thresholds are far lower than download — typical home connections
// are asymmetric and only catch-up TV / DVR services need decent upload.
function uploadRatingColor(mbps) {
  if (mbps >= 20) return "text-emerald-400";
  if (mbps >= 10) return "text-lime-400";
  if (mbps >= 3) return "text-amber-400";
  return "text-rose-400";
}

function uploadRatingText(mbps) {
  if (mbps >= 20) return "Excellent";
  if (mbps >= 10) return "Good";
  if (mbps >= 3) return "Fair";
  return "Slow";
}

export default function SpeedTest() {
  const [phase, setPhase] = useState("idle"); // idle | latency | download | upload | done | error
  const [latency, setLatency] = useState(null);
  const [bandwidth, setBandwidth] = useState(null); // download bps
  const [uploadBps, setUploadBps] = useState(null); // upload bps
  const [progress, setProgress] = useState(0); // current sample size label
  const [error, setError] = useState("");

  const run = async () => {
    setError("");
    setLatency(null);
    setBandwidth(null);
    setUploadBps(null);
    setProgress(0);

    try {
      setPhase("latency");
      const lat = await measureLatency();
      setLatency(lat);

      // ===== Download =====
      setPhase("download");
      let bestDown = 0;
      for (const { bytes, label } of DOWN_SIZES) {
        setProgress(label);
        const t = performance.now();
        const got = await fetchBytes(
          `${SPEEDTEST_HOST}/speedtest/down/${bytes}?t=${Date.now()}`
        );
        const elapsed = (performance.now() - t) / 1000;
        if (elapsed > 0) {
          const bps = (got * 8) / elapsed;
          if (bps > bestDown) bestDown = bps;
          setBandwidth(bps);
        }
        if (elapsed > 8) break; // bail out on slow links
      }

      // ===== Upload =====
      setPhase("upload");
      let bestUp = 0;
      for (const { bytes, label } of UP_SIZES) {
        setProgress(label);
        try {
          const { bps, elapsed } = await uploadBytes(bytes);
          if (bps > bestUp) bestUp = bps;
          setUploadBps(bps);
          if (elapsed > 8) break; // bail out on very slow uploads
        } catch (uerr) {
          // Some restrictive networks block POST to speed.cloudflare.com;
          // record nothing and move on rather than failing the whole test.
          if (bestUp === 0) setUploadBps(null);
          break;
        }
      }

      setPhase("done");
    } catch (e) {
      setError(e?.message || "Speed test failed");
      setPhase("error");
    }
  };

  const downMbps = bandwidth ? bandwidth / 1_000_000 : 0;
  const upMbps = uploadBps ? uploadBps / 1_000_000 : 0;
  const running =
    phase === "latency" || phase === "download" || phase === "upload";

  return (
    <section
      data-testid="settings-speedtest"
      className="bg-[#11141b] border border-white/5 rounded-2xl p-5"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-2">
          <Gauge className="w-4 h-4 text-orange-400" />
          Internet speed test
        </h3>
        <button
          data-testid="settings-speedtest-run"
          onClick={run}
          disabled={running}
          className="text-xs bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white px-3 py-1.5 rounded-md flex items-center gap-1.5"
        >
          {running ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Play className="w-3.5 h-3.5" />
          )}
          {running ? "Running…" : phase === "done" ? "Run again" : "Start"}
        </button>
      </div>

      {/* 2-column grid on phones, 4-column on tablets/desktop so all four
          stats line up without crowding the mobile UI. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Latency" value={latency ? `${latency} ms` : "—"} hint="lower is better" />
        <Stat
          label={
            <span className="inline-flex items-center gap-1">
              <ArrowDown className="w-3 h-3 text-emerald-400" />
              Download
            </span>
          }
          testId="settings-speedtest-download"
          value={
            bandwidth ? (
              <span className={downloadRatingColor(downMbps)}>
                {fmtMbps(bandwidth)}{" "}
                <span className="text-[10px] text-slate-500 uppercase">Mbps</span>
              </span>
            ) : (
              "—"
            )
          }
          hint={bandwidth ? downloadRatingText(downMbps) : "Mbps"}
        />
        <Stat
          label={
            <span className="inline-flex items-center gap-1">
              <ArrowUp className="w-3 h-3 text-sky-400" />
              Upload
            </span>
          }
          testId="settings-speedtest-upload"
          value={
            uploadBps ? (
              <span className={uploadRatingColor(upMbps)}>
                {fmtMbps(uploadBps)}{" "}
                <span className="text-[10px] text-slate-500 uppercase">Mbps</span>
              </span>
            ) : (
              "—"
            )
          }
          hint={uploadBps ? uploadRatingText(upMbps) : "Mbps"}
        />
        <Stat
          label="Status"
          value={
            phase === "idle"
              ? "Idle"
              : phase === "latency"
              ? "Pinging…"
              : phase === "download"
              ? `Down ${progress}`
              : phase === "upload"
              ? `Up ${progress}`
              : phase === "done"
              ? "Done"
              : "Error"
          }
          hint={error || ""}
        />
      </div>

      <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
        Measures the device&apos;s real internet speed against the ZA Player Pro
        backend ({SPEEDTEST_HOST.replace(/^https?:\/\//, "").replace(/\/api$/, "")}).
        This reflects bandwidth on the connection the app actually uses for
        licensing, playlist sync, and admin traffic.
      </p>
    </section>
  );
}

function Stat({ label, value, hint, testId }) {
  return (
    <div data-testid={testId} className="bg-black/40 border border-white/5 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-base font-bold mt-0.5">{value}</div>
      {hint && <div className="text-[10px] text-slate-600 mt-0.5">{hint}</div>}
    </div>
  );
}
